import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import '../css/music-upload.css';
import { fetchClasses } from '../utils/scheduleData';
import { fetchStaff } from '../utils/staffData';

const ACCEPTED_EXTENSIONS = '.mp3,.wav,.m4a,.aac,.ogg,.flac,.wma';
const OTHER_INSTRUCTOR = '__other__';

export default function MusicUpload() {
    const [classes, setClasses] = useState(null);
    const [classesError, setClassesError] = useState(null);
    const [staff, setStaff] = useState(null);
    const [staffError, setStaffError] = useState(null);

    const [adminUser, setAdminUser] = useState(undefined);
    const [instructorChoice, setInstructorChoice] = useState('');
    const [customInstructorName, setCustomInstructorName] = useState('');
    const [className, setClassName] = useState('');
    const [songName, setSongName] = useState('');
    const [file, setFile] = useState(null);
    const [fileInputKey, setFileInputKey] = useState(0);

    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null); // { ok: true, filename } | { ok: false, error }

    useEffect(() => {
        let cancelled = false;
        fetch('/api/admin/session').then((res) => res.ok ? res.json() : null).then((user) => {
            if (cancelled) return;
            setAdminUser(user);
            if (!user) return;
            fetchClasses('fall')
                .then((data) => { if (!cancelled) setClasses(data); })
                .catch((err) => { if (!cancelled) setClassesError(err.message); });
            fetchStaff()
                .then((data) => { if (!cancelled) setStaff(data); })
                .catch((err) => { if (!cancelled) setStaffError(err.message); });
        });
        return () => { cancelled = true; };
    }, []);

    const classNames = useMemo(() => {
        if (!classes) return [];
        return [...new Set(classes.map((c) => c.name))].sort((a, b) => a.localeCompare(b));
    }, [classes]);

    const instructorName = instructorChoice === OTHER_INSTRUCTOR ? customInstructorName : instructorChoice;

    async function handleSubmit(e) {
        e.preventDefault();
        if (!file) {
            setResult({ ok: false, error: 'Please choose a music file to upload.' });
            return;
        }

        setSubmitting(true);
        setResult(null);

        const formData = new FormData();
        formData.append('instructorName', instructorName);
        formData.append('className', className);
        formData.append('songName', songName);
        formData.append('file', file);

        try {
            const res = await fetch('/api/music-upload', { method: 'POST', body: formData });
            const data = await res.json();
            if (!res.ok) {
                setResult({ ok: false, error: data.error || 'Upload failed.' });
            } else {
                setResult({ ok: true, filename: data.filename, songName });
                // Keep the instructor filled in (same person often submits
                // several songs back to back) but clear everything specific to
                // the song they just sent, so it's obvious a fresh one is needed.
                setClassName('');
                setSongName('');
                setFile(null);
                setFileInputKey((k) => k + 1);
            }
        } catch (err) {
            setResult({ ok: false, error: 'Upload failed. Please check your connection and try again.' });
        } finally {
            setSubmitting(false);
        }
    }

    if (adminUser === undefined) return <div className="musicUpload-content"><p>Checking Admin access…</p></div>;
    if (!adminUser) return <div className="musicUpload-content"><Helmet><title>In Motion Dance | Admin Required</title></Helmet><h1>Admin sign-in required</h1><p className="musicUpload-subtitle">Please sign in through the Admin area before uploading music.</p><Link to="/admin">Go to Admin sign-in</Link></div>;

    return (
        <div className="musicUpload-content">
            <Helmet>
                <title>In Motion Dance | Recital Music Upload</title>
            </Helmet>
            <h1>Recital Music Upload</h1>
            <p className="musicUpload-subtitle">For instructors — submit a song for the recital.</p>
            <p className="musicUpload-instructions">
                This form takes <strong>one song at a time</strong>. If you have more than one song
                to submit, fill this out again for each additional song.
            </p>
            <p className="musicUpload-instructions">
                <strong>Where files go:</strong> each song is emailed as an attachment to the studio inbox.
                Find it later by searching the inbox for the class, instructor, or song name included in the attachment filename.
            </p>

            <form className="musicUpload-form" onSubmit={handleSubmit}>
                <label>
                    Your Name
                    {staffError ? (
                        <p className="musicUpload-inlineError">
                            Couldn't load the instructor list ({staffError}). Please refresh.
                        </p>
                    ) : (
                        <select
                            value={instructorChoice}
                            onChange={(e) => setInstructorChoice(e.target.value)}
                            required
                            disabled={!staff}
                        >
                            <option value="" disabled>
                                {staff ? 'Select your name…' : 'Loading instructors…'}
                            </option>
                            {(staff || []).map((s) => (
                                <option key={s.name} value={s.name}>{s.name}</option>
                            ))}
                            <option value={OTHER_INSTRUCTOR}>Other (not listed)</option>
                        </select>
                    )}
                    {instructorChoice === OTHER_INSTRUCTOR && (
                        <input
                            type="text"
                            className="musicUpload-otherName"
                            placeholder="Type your name"
                            value={customInstructorName}
                            onChange={(e) => setCustomInstructorName(e.target.value)}
                            required
                        />
                    )}
                </label>

                <label>
                    Class
                    {classesError ? (
                        <p className="musicUpload-inlineError">
                            Couldn't load the class list ({classesError}). Please refresh.
                        </p>
                    ) : (
                        <select
                            value={className}
                            onChange={(e) => setClassName(e.target.value)}
                            required
                            disabled={!classes}
                        >
                            <option value="" disabled>
                                {classes ? 'Select a class…' : 'Loading classes…'}
                            </option>
                            {classNames.map((name) => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </select>
                    )}
                </label>

                <label>
                    Song Name
                    <input
                        type="text"
                        value={songName}
                        onChange={(e) => setSongName(e.target.value)}
                        required
                    />
                </label>

                <label>
                    Music File
                    <input
                        key={fileInputKey}
                        type="file"
                        accept={ACCEPTED_EXTENSIONS}
                        onChange={(e) => setFile(e.target.files[0] || null)}
                        required
                    />
                    <span className="musicUpload-hint">MP3, WAV, M4A, AAC, OGG, FLAC, or WMA — up to 18MB (email attachment limit). Most songs as MP3 are well under this. One song per submission.</span>
                </label>

                <button type="submit" disabled={submitting}>
                    {submitting ? 'Uploading…' : 'Upload Song'}
                </button>

                {result && result.ok && (
                    <p className="musicUpload-success">
                        ✅ Successfully submitted! "{result.songName}" has been sent to the studio.
                        Have another song? Fill out the form again.
                    </p>
                )}
                {result && !result.ok && (
                    <p className="musicUpload-error">{result.error}</p>
                )}
            </form>
        </div>
    )
}
